import { Backdrop, CircularProgress } from "@mui/material";
import { Suspense, lazy } from "react";
import { Routes as ReactRouterRoutes, Route, HashRouter as Router } from "react-router-dom";

import { AuthenticatedRoute, ChangePassword, ForgotPassword, ResetPassword } from "@sal/portal";
import ErrorBoundary from "components/ErrorBoundary";
import RequireCompanyAccess from "components/RequireCompanyAccess";
import RequireCv2Access from "components/RequireCv2Access";
import PersonnelAndFleetPage from "pages/personelAndFleet/PersonnelAndFleetPage";

const PageLayout = lazy(() => import("./PageLayout"));
const Home = lazy(() => import("./pages/home/Home"));
const BookingsPage = lazy(() => import("./pages/booking/BookingsPage"));
const MyProfile = lazy(() => import("./pages/myProfile/MyProfile"));
const MyPreferencesPage = lazy(() => import("./pages/myPreferences/MyPreferencesPage"));
const AccountsAndRoles = lazy(() => import("./components/myProfile/AccountsAndRoles"));
const RequestCompanyAccountPage = lazy(
	() => import("./pages/requestCompanyAccount/RequestCompanyAccountPage")
);
const NoCompanyAccessPage = lazy(() => import("./pages/noCompany/NoCompanyAccessPage"));
const AccessPendingApprovalPage = lazy(
	() => import("./pages/accessPending/AccessPendingApprovalPage")
);
const UpdatePreferencesPage = lazy(() => import("./pages/updatePreferences/UpdatePreferencesPage"));
const SalCoreLoginPage = lazy(() => import("./pages/SalCoreLoginPage"));
const SsoCallbackPage = lazy(() => import("./pages/SsoCallbackPage"));
const RedirectToMyKrLogin = lazy(() => import("./pages/RedirectToMyKrLogin"));
const NotFoundPage = lazy(() => import("./pages/notFound/NotFoundPage"));

export default function Routes() {
	return (
		<Router>
			{/*
			 * Inside the Router and outside the Suspense on purpose: a stale-build chunk 404 can
			 * reject while loading the lazily-loaded PageLayout itself, not just a page, and that
			 * rejection surfaces at the Suspense boundary. Wrapping it catches both.
			 */}
			<ErrorBoundary>
				<Suspense
					fallback={
						<Backdrop invisible open>
							<CircularProgress />
						</Backdrop>
					}
				>
					<ReactRouterRoutes>
						<Route element={<PageLayout />}>
							<Route element={<AuthenticatedRoute />}>
								<Route element={<RequireCv2Access />}>
									<Route element={<RequireCompanyAccess />}>
										<Route path="/" element={<Home />} />
										<Route
											path="/Bookings"
											element={
												<AuthenticatedRoute
													securityContext="Routes"
													securityContextPermission="Bookings"
												>
													<BookingsPage />
												</AuthenticatedRoute>
											}
										/>
										<Route
											path="/PersonnelAndFleet"
											element={
												<AuthenticatedRoute
													securityContext="Routes"
													securityContextPermission="PersonnelAndFleet"
												>
													<PersonnelAndFleetPage />
												</AuthenticatedRoute>
											}
										/>
										{/* Logged-in access only — no permission gate (same as /MyProfile). */}
										<Route path="/MyPreferences" element={<MyPreferencesPage />} />
									</Route>
									<Route path="/MyProfile" element={<MyProfile />} />
									<Route path="/NoCompanyAccess" element={<NoCompanyAccessPage />} />
									<Route path="/AccessPending" element={<AccessPendingApprovalPage />} />
									<Route path="/RequestCompanyAccount" element={<RequestCompanyAccountPage />} />
								</Route>
							</Route>
						</Route>
						{/* PORTAL FRAMEWORK PAGES*/}
						{/*
						 * Hidden username/password back-door for CV2. NOT linked from anywhere
						 * — reachable only by typing the URL. The normal sign-in path is MyKR
						 * (which itself has the same hidden page). /SsoCallback is exposed too
						 * so the SAL.Login provider can complete its redirect dance when this
						 * back-door is used.
						 */}
						{/*
						 * Public (no-login) Update Preferences flow. Deliberately outside the
						 * PageLayout / AuthenticatedRoute nesting — reached via a link in CV Portal
						 * emails carrying ?ssoid= and ?companyId=. See the Update Preferences flow ticket.
						 */}
						<Route path="/UpdatePreferences" element={<UpdatePreferencesPage />} />
						<Route path="/SalCoreLogin" element={<SalCoreLoginPage />} />
						<Route path="/SsoCallback" element={<SsoCallbackPage />} />
						<Route path="/Login" element={<RedirectToMyKrLogin />} />
						<Route path="/ForgotPassword" element={<ForgotPassword />} />
						<Route path="/ChangePassword" element={<ChangePassword />} />
						<Route path="/ResetPassword/:passwordResetToken" element={<ResetPassword />} />
						<Route path="/NotFound" element={<NotFoundPage />} />
						<Route path="*" element={<NotFoundPage />} />
						{/* /PORTAL FRAMEWORK PAGES*/}
					</ReactRouterRoutes>
				</Suspense>
			</ErrorBoundary>
		</Router>
	);
}
